import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { api } from '../api';
import {
  EmptyState,
  ErrorBox,
  Loader,
  PageHeader,
  StatusBadge,
  formatDateTime,
} from '../components/Common';
import type { BookingStatus } from '../types';

type Filter = 'active' | 'all';

const ACTIVE_STATUSES: BookingStatus[] = ['pending', 'confirmed', 'tentative'];

export function MyBookingsPage() {
  const [filter, setFilter] = useState<Filter>('active');
  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['my-bookings', 'list'],
    queryFn: () => api.bookings.my(true),
  });

  const filtered = (data ?? []).filter((b) =>
    filter === 'active' ? ACTIVE_STATUSES.includes(b.status) : true,
  );

  return (
    <div>
      <PageHeader title="Мои записи" />
      <div className="mb-3 flex gap-2">
        <FilterChip active={filter === 'active'} onClick={() => setFilter('active')}>
          Активные
        </FilterChip>
        <FilterChip active={filter === 'all'} onClick={() => setFilter('all')}>
          Все
        </FilterChip>
      </div>

      {isLoading && <Loader />}
      {error && <ErrorBox message={(error as Error).message} onRetry={() => void refetch()} />}
      {!isLoading && filtered.length === 0 && (
        <EmptyState
          icon="📋"
          title="Нет записей"
          hint={filter === 'active' ? 'Откройте каталог и запишитесь' : ''}
        />
      )}

      <ul className="space-y-2">
        {filtered.map((b) => (
          <li key={b.id}>
            <Link
              to={`/bookings/${b.id}`}
              className="card flex items-center justify-between active:opacity-80"
            >
              <div>
                <p className="font-semibold">{b.service?.name ?? 'Услуга'}</p>
                <p className="text-xs text-tg-hint">{formatDateTime(b.slot_start)}</p>
                <p className="text-xs text-tg-hint">
                  Мастер: {b.master?.first_name ?? ''}
                </p>
              </div>
              <StatusBadge status={b.status} />
            </Link>
          </li>
        ))}
      </ul>
    </div>
  );
}

function FilterChip({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      onClick={onClick}
      className={`rounded-full border px-3 py-1 text-xs font-medium transition ${
        active
          ? 'border-tg-button bg-tg-button text-tg-button-text'
          : 'border-tg-hint/30 text-tg-hint'
      }`}
    >
      {children}
    </button>
  );
}
